/**
 * Video Utility Functions
 * Helper functions for video processing, thumbnail generation, and upload
 */

import viagsApiDev from "../../chats/api/axiosClientdev";

interface UploadResponse {
  StatusCode: number;
  Message: string;
  TotalRecord: number;
  Data: string[];
}

/**
 * Generate thumbnail from video file
 * @param file - Video file to generate thumbnail from
 * @returns Promise resolving to thumbnail blob
 */
export const generateVideoThumbnail = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    video.src = URL.createObjectURL(file);

    video.addEventListener("loadedmetadata", () => {
      const targetWidth = 320;
      const targetHeight = (video.videoHeight / video.videoWidth) * targetWidth;
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Seek to 2 seconds
      video.currentTime = 2;
    });

    video.addEventListener("seeked", () => {
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(video.src);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to create thumbnail blob"));
            }
          },
          "image/jpeg",
          0.7,
        );
      }
    });

    video.addEventListener("error", () => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Video error occurred"));
    });
  });
};

/**
 * Upload thumbnail blob to server using new multi-file API
 * @param blob - Thumbnail blob to upload
 * @returns Promise resolving to uploaded thumbnail URL
 */
export const uploadThumbnail = async (blob: Blob): Promise<string> => {
  const formData = new FormData();
  formData.append("files", blob, `thumbnail_${Date.now()}.jpg`);

  try {
    const response = await viagsApiDev.post<UploadResponse>("/api/file/upload-multiple-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.Data?.[0] || "";
  } catch (error) {
    console.error("Error uploading thumbnail:", error);
    return "";
  }
};
